import { createContext, useContext } from "react";

import { noop } from "~/utils/helpers";

import { initialState } from "./state";

import type { MediaPlayerState } from "./state";
import type {
  MediaCaptionConfig,
  MediaPlayerFullscreenWindowPosition,
  MediaPlayerViewMode,
} from "./types";

export interface MediaPlayerContextValue extends MediaPlayerState {
  mediaElement: HTMLVideoElement | HTMLAudioElement | null;
  pause: () => void;
  play: () => void;
  scrub: (timestamp: number) => void;
  seek: (timestamp: number) => void;
  setCaptions: (config?: MediaCaptionConfig) => void;
  setPlaybackRate: (playbackRate: number) => void;
  setVolume: (volume: number) => void;
  setViewMode: ((viewMode?: MediaPlayerViewMode) => void) | null;
  setViewModeFullscreenSlides:
    | ((viewModeFullscreenSlides: boolean) => void)
    | null;
  setViewModeFullscreenWindowPosition:
    | ((
        viewModeFullscreenWindowPosition: MediaPlayerFullscreenWindowPosition,
      ) => void)
    | null;
  setSeeking: (seeking: boolean) => void;
  setSeekingTime: (seekingTime: number) => void;
}

export const initialValue = {
  ...initialState,
  mediaElement: null,
  pause: () => noop,
  play: () => noop,
  scrub: () => noop,
  seek: () => noop,
  setCaptions: () => noop,
  setPlaybackRate: () => noop,
  setVolume: () => noop,
  setViewMode: null,
  setViewModeFullscreenSlides: null,
  setViewModeFullscreenWindowPosition: null,
  setSeeking: () => noop,
  setSeekingTime: () => noop,
};

export const MediaPlayerContext =
  createContext<MediaPlayerContextValue>(initialValue);

export const useMediaPlayerContext = () => {
  return useContext(MediaPlayerContext);
};
